/**
 * Authentication service
 * Handles login, signup, and user management via Firebase
 *
 * Uses Firebase Auth + Firestore user profiles only.
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
  collection,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { genCode, getCurrentDate } from "../utils/helpers";

const getFriendlyAuthError = (error, fallbackMessage) => {
  const code = error?.code || "";

  switch (code) {
    case "auth/invalid-email":
      return "Invalid email format. Please check and try again.";
    case "auth/user-not-found":
      return "No account found with this email.";
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Incorrect password or email. Please try again.";
    case "auth/user-disabled":
      return "This account is disabled. Please create another account or contact support.";
    case "auth/too-many-requests":
      return "Too many attempts. Please wait a moment and try again.";
    case "auth/network-request-failed":
      return "Network error. Check your internet connection and try again.";
    case "auth/email-already-in-use":
      return "This email is already registered. Try signing in instead.";
    case "auth/weak-password":
      return "Password is too weak. Use at least 6 characters.";
    case "auth/operation-not-allowed":
      return "Email/password sign-in is not enabled in Firebase settings.";
    default:
      return error?.message || fallbackMessage;
  }
};

/**
 * Doctor login
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} User object with role
 */
export const loginDoctor = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    const userSnap = await getDoc(doc(db, "users", userId));

    if (!userSnap.exists()) throw new Error("User profile not found.");
    const profile = userSnap.data();
    if (profile.role !== "doctor") throw new Error("This account is not a doctor account.");

    return { id: userId, ...profile, role: "doctor" };
  } catch (error) {
    throw new Error(getFriendlyAuthError(error, "Doctor login failed. Please try again."));
  }
};

/**
 * Patient login
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} User object with role
 */
export const loginPatient = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    const userSnap = await getDoc(doc(db, "users", userId));

    if (!userSnap.exists()) throw new Error("User profile not found.");
    const profile = userSnap.data();
    if (profile.role !== "patient") throw new Error("This account is not a patient account.");

    return { id: userId, ...profile, role: "patient" };
  } catch (error) {
    throw new Error(getFriendlyAuthError(error, "Patient login failed. Please try again."));
  }
};

/**
 * Create doctor account
 * @param {string} name
 * @param {string} email
 * @param {string} password
 * @returns {Promise<Object>} New doctor with generated code
 */
export const signupDoctor = async (name, email, password) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    const doctorCode = genCode();

    const profile = {
      name,
      email,
      role: "doctor",
      doctorCode,
      patientIds: [],
      createdAt: serverTimestamp(),
    };

    await setDoc(doc(db, "users", userId), profile);
    return { id: userId, ...profile, role: "doctor" };
  } catch (error) {
    throw new Error(getFriendlyAuthError(error, "Doctor signup failed. Please try again."));
  }
};

/**
 * Create patient account
 * @param {string} name
 * @param {string} email
 * @param {string} password
 * @param {string} doctorCode
 * @returns {Promise<Object>} New patient
 */
export const signupPatient = async (name, email, password, doctorCode) => {
  try {
    const normalizedCode = doctorCode.toUpperCase();
    const doctorQ = query(
      collection(db, "users"),
      where("role", "==", "doctor"),
      where("doctorCode", "==", normalizedCode)
    );
    const doctorSnap = await getDocs(doctorQ);

    if (doctorSnap.empty) throw new Error("Invalid doctor code.");
    const doctorDoc = doctorSnap.docs[0];
    const doctorId = doctorDoc.id;

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;

    const profile = {
      name,
      email,
      role: "patient",
      doctorId,
      age: null,
      joinedDate: getCurrentDate(),
      createdAt: serverTimestamp(),
    };

    await setDoc(doc(db, "users", userId), profile);

    return { id: userId, ...profile, role: "patient" };
  } catch (firebaseError) {
    if (firebaseError?.code === "permission-denied") {
      throw new Error("We could not complete signup right now due to a server permission issue. Please try again in a moment.");
    }

    if (firebaseError?.code === "failed-precondition") {
      throw new Error("Setup is still in progress. Please try again shortly.");
    }

    throw new Error(getFriendlyAuthError(firebaseError, "Patient signup failed. Please try again."));
  }
};

export const logout = async () => {
  await signOut(auth);
};
